"use client";

import { useTranslations } from "next-intl";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef } from "react";

import { PreviewPlayerButton } from "@ui/components/core/PreviewPlayer/PreviewPlayerButton";
import { PreviewPlayerProgressRing } from "@ui/components/core/PreviewPlayer/PreviewPlayerProgressRing";
import { useVideoPlaybackState } from "@ui/components/core/PreviewPlayer/useVideoPlaybackState";

// A bespoke <video> rather than the shared LazyVideoPlayer: only the settled front card ever
// mounts this, and it composites a talking-head fill plus a custom play/pause control inside the
// deck's clipped, transformed card. LazyVideoPlayer's in-view lazy mount and AspectRatio wrapper
// would fight that layout, so a raw element is the right fit here.
export function DeckVideo({
  loop = false,
  onEnableSound,
  onEnded,
  soundOn,
  src,
}: {
  loop?: boolean;
  onEnableSound: () => void;
  onEnded: () => void;
  soundOn: boolean;
  src: string;
}) {
  const tGlobal = useTranslations("ClientKeys.Globals");
  const videoRef = useRef<HTMLVideoElement>(null);
  const { currentTime, duration, isPlaying, progressAnimationStartedAt } = useVideoPlaybackState(videoRef);

  // The clip always mounts muted so browsers allow the autoplay; unmute it here once the viewer has
  // enabled sound. Driving `muted` from deck-level state also keeps sound on as the deck auto-advances.
  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.muted = !soundOn;
    }
  }, [soundOn]);

  // First click enables sound (starting playback if it was paused); after that the control is a plain
  // play/pause toggle for the now-unmuted clip.
  function handleClick() {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (!soundOn) {
      onEnableSound();

      if (video.paused) {
        void video.play();
      }

      return;
    }

    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }

  // Keep the press from starting a deck drag or registering as a card tap; the toggle itself runs
  // on click so keyboard activation (Enter/Space fire click, not pointerdown) works too.
  function blockPress(event: ReactPointerEvent<HTMLButtonElement>) {
    event.stopPropagation();
  }

  // The icon reflects sound engagement — ▶ while the clip plays muted (click to enable sound), ⏸ once
  // it plays with sound — while the progress ring tracks the actual clip playback regardless of mute.
  const isEngaged = soundOn && isPlaying;
  const label = isEngaged ? tGlobal("pause") : tGlobal("play");

  return (
    <>
      <video
        autoPlay
        className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover'
        loop={loop}
        muted
        onEnded={onEnded}
        playsInline
        ref={videoRef}
        src={src}
      />
      {/* Reuses the shared PreviewPlayer control so the deck's play/pause matches the dubbing demo,
          including the circular playback-progress ring drawn around the button. */}
      <button
        aria-label={label}
        aria-pressed={isEngaged}
        className='tw-group/preview-button tw-pointer-events-auto tw-absolute tw-bottom-3 tw-right-3 tw-rounded-full tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-outline-focus'
        onClick={handleClick}
        onPointerDown={blockPress}
        title={label}
        type='button'
      >
        <PreviewPlayerButton isActive={isEngaged} size='sm'>
          <PreviewPlayerProgressRing
            currentTime={currentTime}
            duration={duration}
            isActive={isPlaying}
            progressAnimationStartedAt={progressAnimationStartedAt}
            size='sm'
          />
        </PreviewPlayerButton>
      </button>
    </>
  );
}
